import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert/Alert';
import Autocomplete from '@mui/material/Autocomplete/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Link from '@mui/material/Link/Link';
import Stack from '@mui/material/Stack/Stack';
import Switch from '@mui/material/Switch/Switch';
import TextField from '@mui/material/TextField/TextField';
import Typography from '@mui/material/Typography/Typography';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import { AppPath } from '@/shared/constants/path';
import Product from '@/shared/types/products/product.type';
import ProductVariant from '@/shared/types/products/productVariant.type';

import InventoryTableRow from '../../types/inventoryTableRow.type';
import useMapInventoryToProductDialog from './hooks/useMapInventoryToProductDialog';

type MapInventoryToProductDialogProps = {
  product: Product | null;
  initialData?: InventoryTableRow;
  errors?: string[];
  loading?: boolean;
  onClose: () => void;
  onMapProduct: (value: Partial<ProductVariant>) => void;
};

const MapInventoryToProductDialog: FC<MapInventoryToProductDialogProps> = ({
  product,
  initialData,
  errors,
  loading,
  onClose,
  onMapProduct,
}) => {
  const { t } = useTranslation(['products', 'common']);

  const {
    dataIsLoading,
    inventoryList,
    selectedVariant,
    isSellOutOfStock,
    selectedInventory,
    inventorySearchValue,
    onInventorySearchValueChange,
    setSelectedVariant,
    setIsSellOutOfStock,
    setSelectedInventory,
  } = useMapInventoryToProductDialog({ initialData, product });

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('products:mapInventoryDialogTitle'),
        content:
          errors && errors.length > 0 ? (
            <Stack spacing={2}>
              {errors.map((error, index) => (
                <Alert key={index} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ) : (
            <Stack sx={{ py: 1 }} spacing={2}>
              <Autocomplete
                loading={!product?.variants}
                value={selectedVariant}
                onChange={(_, newValue) => setSelectedVariant(newValue)}
                options={product?.variants || []}
                getOptionLabel={(option) =>
                  option.variant_name === '_default'
                    ? option.product_name
                    : option.variant_name
                }
                renderOption={(props, option) => {
                  return (
                    <Stack component='li' {...props} key={option.variant_id}>
                      <Stack sx={{ width: '100%' }}>
                        <Typography variant='body2' color='primary' noWrap>
                          {option.variant_name === '_default'
                            ? option.product_name
                            : `${option.product_name}: ${option.variant_name}`}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Product ID: {option.product_id}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Variant ID: {option.variant_id.split('-')[0]}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Product/Variant'
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <SearchIcon
                          sx={{
                            width: 20,
                            height: 20,
                            color: 'text.secondary',
                          }}
                        />
                      ),
                      endAdornment: dataIsLoading && (
                        <CircularProgress size={16} />
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                loading={!inventoryList}
                inputValue={inventorySearchValue}
                onInputChange={(_, value, reason) => {
                  onInventorySearchValueChange(value, reason);
                }}
                value={selectedInventory}
                onChange={(_, newValue) => setSelectedInventory(newValue)}
                filterOptions={(item) => item}
                options={inventoryList || []}
                getOptionLabel={(option) => option.inventory_name}
                renderOption={(props, option) => {
                  return (
                    <Stack component='li' {...props} key={option.inventory_id}>
                      <Stack sx={{ width: '100%' }}>
                        <div>
                          <Typography variant='body2' color='primary' noWrap>
                            {option.inventory_name}
                          </Typography>
                          <Stack
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'auto minmax(200px, auto)',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                            }}
                          >
                            <Typography variant='body2' color='text.secondary'>
                              Inventory ID: {option.inventory_id.split('-')[0]}
                            </Typography>

                            <Stack direction='row' spacing={2}>
                              <Typography
                                variant='body2'
                                noWrap
                                color='text.secondary'
                              >
                                Available: {option.available}
                              </Typography>

                              <Typography
                                variant='body2'
                                noWrap
                                color='text.secondary'
                              >
                                Total: {option.total}
                              </Typography>
                            </Stack>
                          </Stack>
                        </div>

                        <Typography
                          variant='body2'
                          noWrap
                          color='text.secondary'
                        >
                          Description: {option.inventory_description}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Select Inventory'
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <SearchIcon
                          sx={{
                            width: 20,
                            height: 20,
                            color: 'text.secondary',
                          }}
                        />
                      ),
                      endAdornment: dataIsLoading && (
                        <CircularProgress size={16} />
                      ),
                    }}
                  />
                )}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isSellOutOfStock}
                    onChange={(e) => setIsSellOutOfStock(e.target.checked)}
                  />
                }
                label='Sell Out of Stock'
              />

              <Link
                sx={{ textTransform: 'uppercase', display: 'flex', gap: 2 }}
                href={`${AppPath.Inventory}/create`}
                target='_blank'
              >
                go to create inventory
                <LaunchIcon sx={{ width: 16 }} />
              </Link>
            </Stack>
          ),
        actions: [
          {
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            label: t('products:map'),
            variant: 'contained',
            icon: loading && (
              <CircularProgress color='inherit' size={16} sx={{ mr: 2 }} />
            ),
            onClick: () => {
              if (!loading && product && selectedVariant && selectedInventory) {
                onMapProduct({
                  ...selectedVariant,
                  is_sell_out_of_stock: isSellOutOfStock,
                  inventory_id: selectedInventory.inventory_id,
                });
              }
            },
          },
        ],
      }}
    />
  );
};

export default MapInventoryToProductDialog;
